import React from "react";
import { useTranslation } from "react-i18next";

import { AsyncImg } from "../../../components/async-img/AsyncImg";
import { Heading } from "../../../components/heading/Heading";
import { ViewSection } from "../../../components/view-section/ViewSection";
import { SubTitle } from "../../../components/sub-title/SubTitle";
import imageA from "./assets/hand_with_mobile.png";
import imageB from "./assets/image_people.png";
import imageC from "./assets/profit_from_pros.png";
import imageD from "./assets/trading1.png";
import { Link } from "../../../components/link/Link";
import IconCheck from "../../../components/icon/IconCheck";
import { IconShieldSuccess } from "../../../components/icon/IconShieldSuccess";

import styles from "./product-digital-trading-intro-section.module.scss";

export interface ProductDigitalTradingIntroSectionProps {}

const benefits = [
  "You have an account with a regulated broker",
  "Your funds are securely in your own account",
  "Super fast deposits and withdrawals",
  "You do not need to purchase a product or subscription",
];

export const ProductDigitalTradingIntroSection: React.FC<ProductDigitalTradingIntroSectionProps> = () => {
  // access translation keys
  const { t } = useTranslation();

  return (
    <div>
      <ViewSection withBackground image={<AsyncImg src={imageB} className={styles.image} />} gap="GAP_60" gutter={60}>
        <Heading className={styles.title} gutter={8} level={4}>
          {t("The Easiest Way to Start Trading — Without the Usual Struggles")}
        </Heading>
        <SubTitle withBottomLine>
          {t("Trading sounds exciting, but for most people, it’s a frustrating struggle.")}
        </SubTitle>
        <div className={styles.content}>
          <div>
            <p>{t("Maybe you’ve thought about trading before but held back because:")}</p>
            <p>{t("You don’t know where to start or how to trade profitably.")}</p>
            <p>{t("You fear losing money like most retail traders do.")}</p>
            <p>{t("You don’t have enough capital to make meaningful profits.")}</p>
            <p>{t("You lack the time or discipline to manage trades properly.")}</p>
            <p>{t("Most trading courses, signal groups, and software cost a fortune.")}</p>
          </div>
          <p>
            {t(
              "These are real concerns — and the reason why many traders quit before they ever see success. But what if there was a way to access more capital, reduce risk, and have the flexibility to trade on your own or follow experienced traders—without needing years of experience or expensive courses?",
            )}
          </p>
          <p>
            {t(
              "With a 12x amplified trading account, you get a larger portfolio to trade with. Whether you prefer to make your own trading decisions or take advantage of automated trading from top-performing traders, the choice is yours.",
            )}
          </p>
        </div>
      </ViewSection>
      <ViewSection image={<AsyncImg src={imageD} />} reverse="ALL_SCREENS" gap="GAP_20" gutter={40}>
        <div>
          <p style={{ marginBottom: "20px" }}>
            <span style={{ fontWeight: "bold" }}>{t("Increase Your Portfolio to 12x Size")}</span>
            <br />
            {t(
              "DAGS collaboration with TAG Markets gives you a unique opportunity. With our 50/50 deposit model — half in USD and half in DAGS — you gain access to a 12x amplified account. That means a $1,000 deposit can give you a $12,000 trading portfolio. This gives you massively increased earning potential, whether you choose to trade manually or use our automated solution. It’s the smart way to level up your capital instantly.",
            )}
            <br />
            <br />
            <Link
              color="DARK_BLUE"
              borderRadius="SMALL"
              weight="BOLD"
              to={""}
              externalUrl={"https://portal.tagmarkets.com/live_signup?sidi=336EE2CF-9636-4BA1-B52F-8EED26326823"}
            >
              {t("Open your Trading Account (FREE)")}
            </Link>
            <br />
            {t(" … and lock in your opportunity for the 50/50 deposit")}
            <br />
            <br />
            {t(
              "*Go to DAGS Community section to read the details on how to cover half of your deposit amount using DAGS, and to benefit greatly from this opportunity.",
            )}
          </p>
          <p style={{ marginBottom: "20px" }}>
            <span style={{ fontWeight: "bold" }}>{t("Trade Manually or Automate — You Choose")}</span>
            <br />
            {t(
              "Once your 12x account is active, you’re free to trade on your own or take advantage of an automatic copy-trading solution. For those who prefer a hands-free experience, automation gives you direct access to the trades of proven, high-performing traders, without lifting a finger. It’s ideal for busy individuals or anyone looking for a more passive way to grow their capital. No need to spend your days learning charts, strategies or watching markets all day.",
            )}
          </p>
        </div>
      </ViewSection>

      <ViewSection gap="GAP_40" gutter={60} separate={false}>
        <div className={styles["benefits-list"]}>
          {benefits.map((item, index) => (
            <div className={styles["benefit-item"]} key={index}>
              <IconShieldSuccess className={styles["icon"]} fill="green" width={48} height={48} />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </ViewSection>
      <ViewSection image={<AsyncImg src={imageC} />} gap="GAP_60" gutter={60}>
        <div>
          <p style={{ marginBottom: "20px" }}>
            <span style={{ fontWeight: "bold" }}>
              {t("No Upfront Fees, No Subscriptions — Purely Performance Based")}
            </span>
            <br />
            {t(
              "Most trading services, signal groups, or so-called experts charge you for their ideas, tools, or software — often before you’ve made a single dollar. That’s not the case here. There are no subscription fees, no software to buy, and no hidden charges. You simply connect your account, and the system automatically mirrors the trades of carefully selected, top-performing traders or robots. You only pay a performance fee of up to 30% if profits are made — fair and transparent.",
            )}
          </p>
          <p style={{ marginBottom: "20px" }}>
            <span style={{ fontWeight: "bold" }}>{t("Built on Real Strategies, Not Gambling")}</span>
            <br />
            {t(
              "The highlighted strategies are not another algorithms or high-risk trading bots. They are either carefully selected professional traders or softwares that have delivered consistent results over the past year.",
            )}
            <br />
            <br />
            {t(
              "All of them are targeting 5–7% monthly growth on the portfolio for steady, long-term progress. Since your portfolio is 12x larger than your deposit, the potential returns on your capital are significantly amplified. With hundreds of trades executed and a 70%+ success rate, the strategies are built for stability and sustainability.",
            )}
          </p>
        </div>
      </ViewSection>
      <ViewSection image={<AsyncImg src={imageA} />} reverse="ALL_SCREENS" gap="GAP_60" gutter={60}>
        <div>
          <p style={{ marginBottom: "20px" }}>
            <span style={{ fontWeight: "bold" }}>{t("How to Get Started")}</span>
            <br />
            {t("Getting started is simple and only takes a few steps:")}
          </p>
          <ol style={{ marginLeft: 0, paddingLeft: "20px" }}>
            <li style={{ marginBottom: "20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "5px" }}>
                <span style={{ marginTop: "10px" }}>{t("Create a free account on TAG Markets ")}</span>
                <Link
                  color="DARK_BLUE"
                  borderRadius="SMALL"
                  weight="BOLD"
                  to={""}
                  externalUrl={"https://portal.tagmarkets.com/live_signup?sidi=336EE2CF-9636-4BA1-B52F-8EED26326823"}
                >
                  {t("Open Account")}
                </Link>
              </div>
            </li>
            <li style={{ marginBottom: "20px" }}>{t("Make a deposit — 50% in USD, 50% in DAGS")}</li>
            <li style={{ marginBottom: "20px" }}>{t("Request a 12x amplified trading account")}</li>
            <li style={{ marginBottom: "20px" }}>
              <div>
                <span style={{ marginTop: "10px" }}>
                  {t("Start trading on your own or subscribe to automation. For example:")}
                </span>
                <ol type="A">
                  <li style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "5px" }}>
                    <span>Multistrategy (AI + human)</span>
                    <Link
                      color="DARK_BLUE"
                      borderRadius="SMALL"
                      weight="BOLD"
                      to={""}
                      externalUrl={"https://social.tagmarkets.com/portal/registration/subscription/83660/iTraderAI"}
                    >
                      {t("Start Copying")}
                    </Link>
                  </li>
                </ol>
              </div>
            </li>
            <li style={{ marginBottom: "20px" }}>{t("Activate and enjoy automated trading from vetted strategies")}</li>
          </ol>
          <p>{t("Sit back and watch the system work for you — completely hands-free.")}</p>
        </div>
      </ViewSection>

      <ViewSection gap="GAP_20" gutter={30} separate={false}>
        <h3 style={{ fontSize: 12 }}>{t("Disclaimer")}</h3>
        <p style={{ fontSize: 12 }}>
          {t(
            "SF Suite does not offer financial services, provide financial or investment advice, nor does it sell any financial products. There is no product sale involved, information on this page is informational, it does not require or solicit a purchase of a product and is purely an introduction of a free of cost opportunity. Nothing presented here should be interpreted as a promise or guarantee of profits. Trading and investing involve risk. Past performance is not a guarantee of future results. The use of leverage amplifies both potential gains and potential losses. The solutions described are designed for users who understand the risks associated with trading. Always trade responsibly and never invest more than you can afford to lose.",
          )}
        </p>
      </ViewSection>
    </div>
  );
};
